import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageShell } from "@/components/PageShell";
import { Check, Undo2, ListChecks, HandHeart } from "lucide-react";

const dayLabel = (iso) => {
  const today = new Date().toISOString().slice(0, 10);
  const d = new Date(iso + "T00:00:00");
  const yest = new Date(); yest.setDate(yest.getDate() - 1);
  if (iso === today) return "Today";
  if (iso === yest.toISOString().slice(0, 10)) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
};

export default function WinLog() {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.winsHistory();
      setDays(res.days);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const undoTask = async (t) => {
    // In case something was marked done by mistake
    await api.updateTask(t.id, { status: "not started" });
    toast("Moved back to your tasks.");
    load();
  };
  const undoDeposit = async (d) => {
    await api.uncompleteTrustDeposit(d.id);
    toast("Reset — no problem.");
    load();
  };

  const totalAll = days.reduce((s, d) => s + d.tasks.length + d.deposits.length, 0);

  return (
    <PageShell title="Win log" subtitle="Everything you've finished. No streaks, no pressure.">
      {loading ? (
        <p className="text-zinc-600 text-sm">Loading…</p>
      ) : totalAll === 0 ? (
        <div className="bg-zinc-800/60 rounded-2xl p-6 text-center" data-testid="winlog-empty">
          <p className="text-zinc-400 text-sm">Nothing logged yet. Finish a task or keep a promise and it'll land here.</p>
        </div>
      ) : (
        <div className="space-y-7" data-testid="winlog-days">
          {days.map((day) => (
            <section key={day.date} data-testid={`winlog-day-${day.date}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-zinc-300 text-sm font-semibold">{dayLabel(day.date)}</h2>
                <span className="text-xs text-zinc-600">{day.tasks.length + day.deposits.length} wins</span>
              </div>
              <div className="space-y-2">
                {day.tasks.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 bg-zinc-100 rounded-xl px-4 py-3" data-testid={`winlog-task-${t.id}`}>
                    <Check className="w-4 h-4 text-[#365458] shrink-0" />
                    <ListChecks className="w-4 h-4 text-zinc-400 shrink-0" />
                    <span className="text-zinc-800 text-sm flex-1 truncate">{t.title}</span>
                    <button
                      data-testid={`winlog-undo-task-${t.id}`}
                      onClick={() => undoTask(t)}
                      className="text-zinc-400 hover:text-zinc-700 inline-flex items-center gap-1 text-xs"
                    >
                      <Undo2 className="w-3.5 h-3.5" /> Undo
                    </button>
                  </div>
                ))}
                {day.deposits.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 bg-zinc-100 rounded-xl px-4 py-3" data-testid={`winlog-deposit-${d.id}`}>
                    <Check className="w-4 h-4 text-[#365458] shrink-0" />
                    <HandHeart className="w-4 h-4 text-zinc-400 shrink-0" />
                    <span className="text-zinc-800 text-sm flex-1 truncate">{d.promise}</span>
                    <button
                      data-testid={`winlog-undo-deposit-${d.id}`}
                      onClick={() => undoDeposit(d)}
                      className="text-zinc-400 hover:text-zinc-700 inline-flex items-center gap-1 text-xs"
                    >
                      <Undo2 className="w-3.5 h-3.5" /> Undo
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </PageShell>
  );
}
